"use client"
import { useState, useEffect } from "react"
import {
  Shield,
  Trash2,
  Mail,
  Clock,
  AlertTriangle,
  FileText,
  Database,
  Info,
  ChevronRight,
  UserX,
  CheckCircle,
  XCircle,
} from "lucide-react"

export const DeleteAccount = () => {
  const pageBnrImg = {
    position: "relative",
    backgroundImage: `url('./media/frontend/img/about/blog/1920x700/banner-2.jpg')`,
    backgroundPosition: "center",
    backgroundSize: "cover",
  }

  const [activeSection, setActiveSection] = useState("")
  const [readingProgress, setReadingProgress] = useState(0)

  const sections = [
    { id: "steps", title: "Deletion Steps", icon: FileText },
    { id: "data", title: "Data We Delete", icon: Database },
    { id: "retention", title: "Data Retention Policy", icon: Clock },
    { id: "disclaimer", title: "Important Disclaimer", icon: Info },
  ]

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.scrollY
      const docHeight = document.documentElement.scrollHeight - window.innerHeight
      const progress = (scrollTop / docHeight) * 100
      setReadingProgress(progress)

      const sectionElements = sections.map((section) => ({
        id: section.id,
        element: document.getElementById(section.id),
      }))

      const currentSection = sectionElements.find((section) => {
        if (section.element) {
          const rect = section.element.getBoundingClientRect()
          return rect.top <= 100 && rect.bottom >= 100
        }
        return false
      })

      if (currentSection) {
        setActiveSection(currentSection.id)
      }
    }

    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId)
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" })
    }
  }

  return (
    <div className="min-h-screen bg-white">
      {/* Reading Progress Bar */}
      <div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-600 to-red-400 z-50 origin-left transition-all duration-100"
        style={{ transform: `scaleX(${readingProgress / 100})` }}
      />

      {/* Enhanced Hero Section */}
      <section className="relative overflow-hidden">
        <div className="pageBanr py-32 relative" style={pageBnrImg}>
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/80"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/20 to-black/70"></div>

          <div className="container mx-auto relative z-[1] text-center px-4 animate-fadeIn">
            <div className="inline-block mb-6">
              <div className="w-20 h-20 bg-gradient-to-br from-red-500/20 to-red-600/40 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20 animate-scaleIn">
                <UserX className="w-10 h-10 text-red-500" />
              </div>
            </div>

            <h2 className="font-bold text-5xl md:text-7xl text-white uppercase tracking-wider mb-6 bg-gradient-to-r from-white via-gray-100 to-white bg-clip-text">
              Delete My Account
            </h2>

            <div className="w-32 h-1 bg-gradient-to-r from-transparent via-red-500 to-transparent mx-auto mb-8"></div>

            <ul className="inline-flex items-center space-x-2 text-white mb-0 bg-black/20 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10">
              <li className="text-lg">
                <a href="/" className="!text-white hover:!text-red-500 transition-all duration-300">
                  Home
                </a>
              </li>
              <li className="text-white/60">/</li>
              <li className="text-lg font-semibold text-red-500">Delete Account</li>
            </ul>

            <p className="text-xl text-white/90 mt-6 max-w-2xl mx-auto">
              Request permanent deletion of your account and associated data.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-gradient-to-br from-gray-50 via-white to-gray-50 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-10 w-64 h-64 bg-gradient-to-br from-red-500/5 to-transparent rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-10 w-80 h-80 bg-gradient-to-br from-red-500/3 to-transparent rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto lg:px-2 px-4 relative z-10">
          <div className="flex lg:flex-row flex-col gap-12">
            {/* Table of Contents - Sticky Sidebar */}
            <div className="lg:w-1/4 w-full">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="w-10 h-10 bg-gradient-to-br from-red-500/10 to-red-600/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-5 h-5 text-red-600" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-800">Contents</h3>
                  </div>

                  <nav className="space-y-2">
                    {sections.map((section) => {
                      const IconComponent = section.icon
                      return (
                        <button
                          key={section.id}
                          onClick={() => scrollToSection(section.id)}
                          className={`w-full flex items-center space-x-3 p-3 rounded-xl text-left transition-all duration-300 group ${
                            activeSection === section.id
                              ? "bg-gradient-to-r from-red-500/10 to-red-500/5 text-red-600 border border-red-500/20"
                              : "hover:bg-gray-50 text-gray-700 hover:text-red-600"
                          }`}
                        >
                          <IconComponent className="w-4 h-4 flex-shrink-0" />
                          <span className="text-sm font-medium flex-1">{section.title}</span>
                          <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                        </button>
                      )
                    })}
                  </nav>
                </div>
              </div>
            </div>

            {/* Main Content */}
            <div className="lg:w-3/4 w-full">
              {/* Introduction */}
              <div className="mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/10 to-red-600/20 rounded-xl flex items-center justify-center">
                      <UserX className="w-6 h-6 text-red-600" />
                    </div>
                    <div>
                      <h1 className="text-3xl font-bold text-red-600 uppercase">Request Account & Data Deletion</h1>
                      <div className="flex items-center space-x-2 mt-2">
                        <Shield className="w-4 h-4 text-gray-500" />
                        <p className="text-base text-gray-700">Developer/App Name: Shefs Cloud</p>
                      </div>
                    </div>
                  </div>
                  <div className="bg-gradient-to-r from-red-500/5 to-transparent rounded-2xl p-6 border-l-4 border-red-500">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
                      <p className="text-lg text-gray-700 leading-relaxed">
                        <strong>Important:</strong> Account deletion is permanent and cannot be undone. All your data will be permanently removed from our systems.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 1: Steps to Request Deletion */}
              <div id="steps" className="mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500/10 to-blue-600/20 rounded-xl flex items-center justify-center">
                      <FileText className="w-6 h-6 text-blue-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 uppercase">Steps to Request Deletion</h2>
                  </div>
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">1</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-lg text-gray-700 leading-relaxed">
                            <strong>Email us</strong> at{" "}
                            <a
                              href="mailto:info@shefscloud.com"
                              className="text-red-600 hover:text-red-700 underline transition-colors duration-300"
                            >
                              info@shefscloud.com
                            </a>{" "}
                            from the email associated with your account.
                          </p>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <div className="flex items-start space-x-4 mb-4">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">2</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-lg text-gray-700 leading-relaxed mb-3">
                            <strong>In the subject line, write:</strong>
                          </p>
                          <div className="bg-gray-100 rounded-xl p-4 border-l-4 border-blue-600">
                            <p className="text-lg font-mono text-gray-800">"Account Deletion Request"</p>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="bg-gradient-to-r from-blue-50/50 to-transparent rounded-2xl p-6">
                      <div className="flex items-start space-x-4">
                        <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-white font-bold">3</span>
                        </div>
                        <div className="flex-1">
                          <p className="text-lg text-gray-700 leading-relaxed">
                            <strong>We will process your request within 7 days</strong> and confirm via email once deletion is complete.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Data We Delete */}
              <div id="data" className="mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-purple-500/10 to-purple-600/20 rounded-xl flex items-center justify-center">
                      <Database className="w-6 h-6 text-purple-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 uppercase">Data We Delete</h2>
                  </div>
                  <div className="bg-gradient-to-r from-purple-50/50 to-transparent rounded-2xl p-6">
                    <p className="text-lg text-gray-700 mb-6">
                      Upon processing your deletion request, we will permanently remove all associated data, including:
                    </p>
                    <ul className="space-y-4">
                      <li className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Profile information</strong> (name, email, phone number, address)
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Uploaded content</strong> (photos, reviews, comments)
                        </span>
                      </li>
                      <li className="flex items-start space-x-3">
                        <XCircle className="w-5 h-5 text-purple-600 mt-0.5 flex-shrink-0" />
                        <span className="text-lg text-gray-700">
                          <strong>Usage data</strong> (order history, preferences, activity logs)
                        </span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Section 3: Data Retention Policy */}
              <div id="retention" className="mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-orange-500/10 to-orange-600/20 rounded-xl flex items-center justify-center">
                      <Clock className="w-6 h-6 text-orange-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 uppercase">Data Retention Policy</h2>
                  </div>
                  <div className="bg-gradient-to-r from-orange-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Clock className="w-6 h-6 text-orange-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg text-gray-700 leading-relaxed mb-4">
                          While most of your data is deleted immediately, we retain some non-identifiable logs for audit and fraud prevention purposes.
                        </p>
                        <div className="bg-orange-100/50 rounded-xl p-4 border-l-4 border-orange-500">
                          <p className="text-lg text-gray-700">
                            <strong>Retention Period:</strong> Non-identifiable logs are kept for up to <strong>30 days after deletion</strong>, then permanently removed.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 4: Disclaimer */}
              <div id="disclaimer" className="mb-12">
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-white/50 hover:shadow-2xl transition-shadow duration-500">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-green-500/10 to-green-600/20 rounded-xl flex items-center justify-center">
                      <Info className="w-6 h-6 text-green-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 uppercase">Important Disclaimer</h2>
                  </div>
                  <div className="bg-gradient-to-r from-green-50/50 to-transparent rounded-2xl p-6">
                    <div className="flex items-start space-x-4">
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="text-lg text-gray-700 leading-relaxed">
                          <strong>Good news!</strong> Users can also delete their account directly from the app. This feature will be available in the upcoming update, providing you with instant control over your data.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Section */}
              <div className="mb-12">
                <div className="bg-gradient-to-br from-red-500/5 to-red-600/10 backdrop-blur-sm rounded-3xl p-8 shadow-xl border border-red-500/20">
                  <div className="flex items-center space-x-4 mb-6">
                    <div className="w-12 h-12 bg-gradient-to-br from-red-500/20 to-red-600/30 rounded-xl flex items-center justify-center">
                      <Mail className="w-6 h-6 text-red-600" />
                    </div>
                    <h2 className="text-2xl font-bold text-red-600 uppercase">Need Help?</h2>
                  </div>
                  <div className="space-y-4">
                    <p className="text-lg text-gray-700">
                      If you have any questions about the account deletion process or need assistance, please don't hesitate to contact us:
                    </p>
                    <div className="flex items-center space-x-4 bg-white rounded-xl p-4">
                      <div className="w-12 h-12 bg-red-500/10 rounded-xl flex items-center justify-center">
                        <Mail className="w-6 h-6 text-red-600" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 font-medium">Email Support</p>
                        <a
                          href="mailto:info@shefscloud.com"
                          className="text-lg text-red-600 hover:text-red-700 underline transition-colors duration-300 font-semibold"
                        >
                          info@shefscloud.com
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default DeleteAccount